import React, { useContext, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import Loading from "../loading";
import { CollectionName } from "../../api";
import { StoresContext } from "../../pages/_app";
import { Card, HTMLTable } from "@blueprintjs/core";
import AnnotationTableRow from "./row";
import classnames from "classnames";
import { reaction } from "mobx";

interface Props {
  collectionName: CollectionName;
}

export const AnnotationTable = (props: Props) => {
  const rootStore = useContext(StoresContext);
  const annotationStore = rootStore.annotationStore;
  const tableRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    const disposer = reaction(
      () => annotationStore.selectedUuid,
      (uuid) => {
        if (uuid && !annotationStore.jumpedInTable && tableRef.current) {
          const idx = annotationStore.annotations.data.findIndex(
            (a) => a.uuid === annotationStore.selectedUuid
          );
          if (idx >= 0) {
            tableRef.current.scrollTop = 50 * idx;
            annotationStore.tableJumpDone();
          }
        }
      }
    );

    return () => {
      disposer();
    };
  }, []);

  if (annotationStore.loadingAnnotations) {
    return <Loading text="Loading..." />;
  }
  // ATCC only
  if (annotationStore.filteredAnnotations.length === 0 && annotationStore.isAmr) {
    return <div className="no-annotations-message">No AMR genes found</div>;
  }

  const isFungal =
    !!props.collectionName && ["mycology", "protistology"].includes(props.collectionName);

  const renderHeaders = () => {
    const isComparison = annotationStore.isComparison;
    let classes = classnames("annotations-table__header", {
      "annotations-table__header--comparison": isComparison,
    });

    const nameCell = isFungal ? <th /> : <th>Name</th>;
    const externalIDCell = isFungal ? <th>OrthoDB Group</th> : <th>RefSeq ID</th>;
    const ecNumberCell = isFungal ? null : <th>EC Number</th>;
    const jumpCell = isFungal ? null : <th>Jump</th>;

    if (isFungal) {
      classes = classnames("fungal-annotations-table__header");
    }

    return (
      <thead className={classes}>
        <tr>
          {isComparison && <th>Genome</th>}
          <th>Contig</th>
          <th>Start</th>
          <th>End</th>
          {nameCell}
          <th>Protein Product</th>
          {!isFungal && <th />}
          {ecNumberCell}
          <th>Type</th>
          {externalIDCell}
          {jumpCell}
          <th />
        </tr>
      </thead>
    );
  };

  return (
    <Card elevation={1}>
      <HTMLTable className="annotations-table" striped={true}>
        {renderHeaders()}
        <tbody className="annotations-table__body" ref={tableRef}>
          {annotationStore.filteredAnnotations.map((item) => {
            return (
              <AnnotationTableRow
                collectionName={props.collectionName}
                selected={annotationStore.selectedUuid === item.uuid}
                key={item.uuid}
                isComparison={annotationStore.isComparison}
                annotation={item}
                selectAnnotation={() => annotationStore.selectAnnotation(item)}
              />
            );
          })}
        </tbody>
      </HTMLTable>
    </Card>
  );
};

export default observer(AnnotationTable);
